import * as React from "react";
import { Redirect, Route, Switch, useHistory, useRouteMatch } from "react-router-dom";
import {
  AccessOverviewEditChartColorsElementEnum,
  AccessReportsAdminAddProductStepEnum,
  AccessReportsAdminPageOptionsEnum,
  AccessReportsAdminWizardStep,
  AccessSummaryEditChartColorsElementEnum,
} from "@app/access-reports/admin";
import { AccessReportProviderSetModel } from "@app/provider-sets";
import { cloneDeep, flatten, isNil } from "lodash";
import { getAllElementsByPageFromTemplate, getElementByIdFromTemplate, getFormGroupByGroupPropertyIdFromTemplate, getPageFromTemplate } from "@app/access-reports/shared/utility";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import { AccessReportsAdminAddProductDetails } from "@app/access-reports/admin/AccessReportsAdminAddProductDetails";
import { AccessReportsAdminAddProductPageOptions } from "@app/access-reports/admin/AccessReportsAdminAddProductPageOptions";
import { AccessReportsAdminAddProductSpecialties } from "@app/access-reports/admin/AccessReportsAdminAddProductSpecialties";
import { AccessRadiusSearchPageElementEnum } from "@app/access-reports/shared/accessRadiusSearchPageElementEnum";
import { AccessReportsAccessAnalysisElementEnum } from "@app/access-reports/shared/accessReportsAccessAnalysisElementEnum";
import { AccessReportsCoverPageElementEnum } from "@app/access-reports/shared/accessReportsCoverPageElementEnum";
import { AccessReportsPageOptionsElementEnum } from "@app/access-reports/shared/accessReportsPageOptionsElementEnum";
import { AccessReportsServiceArea } from "@app/access-reports/shared/accessReportsServiceArea";
import { AccessReportsWizardAccessCriteria } from "@app/access-reports/shared/accessReportsWizardAccessCriteria";
import { AccessReportsWizardElement } from "@app/access-reports/shared/accessReportsWizardElement";
import { AccessReportsWizardElementTypeEnum } from "@app/access-reports/shared/accessReportsWizardElementTypeEnum";
import { AccessReportsWizardFormGroup } from "@app/access-reports/shared/accessReportsWizardFormGroup";
import { AccessReportsWizardGroupPropertyIdEnum } from "@app/access-reports/shared/accessReportsWizardGroupPropertyIdEnum";
import { AccessReportsWizardNetworkStandard } from "@app/access-reports/shared/accessReportsWizardNetworkStandard";
import { AccessReportsWizardNetworkStandardTypeEnum } from "@app/access-reports/shared/accessReportsWizardNetworkStandardTypeEnum";
import { AccessReportsWizardOption } from "@app/access-reports/shared/accessReportsWizardOption";
import { AccessReportsWizardPage } from "@app/access-reports/shared/accessReportsWizardPage";
import { AccessReportsWizardProduct } from "@app/access-reports/shared/accessReportsWizardProduct";
import { AccessReportsWizardProviderSet } from "@app/access-reports/shared/accessReportsWizardProviderSet";
import { AccessReportsWizardStepEnum } from "@app/access-reports/shared/accessReportsWizardStepEnum";
import { AccessReportsWizardTemplate } from "@app/access-reports/shared/accessReportsWizardTemplate";
import { ByProductDefault } from "@app/access-reports/shared/byProductDefault";
import { ColumnDefaults } from "@app/access-reports/shared/columnDefaults";
import { EmployeeGroupStatusEnum } from "@app/access-reports/shared/employeeGroupStatusEnum";
import { PageSelectionElementIdEnum } from "@app/access-reports/shared/pageSelectionElementIdEnum";

type AccessReportsAdminAddProductProps = {
  productId: number;
  disableUserActions: boolean;
  onSaveProductClick: (template: AccessReportsWizardTemplate) => void;
  template: AccessReportsWizardTemplate;
}

const AccessReportsAdminAddProduct: React.FC<AccessReportsAdminAddProductProps> = (props: AccessReportsAdminAddProductProps) => {
  const history = useHistory();
  const match = useRouteMatch();
  const [filterMemberSetByOutsideServiceArea, setFilterMemberSetByOutsideServiceArea] = React.useState(false);
  const [groups, setGroups] = React.useState<string[]>([]);
  const [hiddenAccessStandards, setHiddenAccessStandards] = React.useState<number[]>([]);
  const [hiddenCalculationMethods, setHiddenCalculationMethods] = React.useState<number[]>([]);
  const [hiddenDistanceMethods, setHiddenDistanceMethods] = React.useState<number[]>([]);
  const [invalidElements, setInvalidElements] = React.useState(new Map<string, string>());
  const [invalidPageOptionPageElements, setInvalidPageOptionPageElements] = React.useState(new Map<string, string>());
  const [product, setProduct] = React.useState<AccessReportsWizardProduct>(null);
  const [productType, setProductType] = React.useState<AccessReportsWizardOption>(null);
  const [productTypes, setProductTypes] = React.useState<AccessReportsWizardOption[]>([]);
  const [reportTitleOne, setReportTitleOne] = React.useState("");
  const [reportTitleThree, setReportTitleThree] = React.useState("");
  const [reportTitleTwo, setReportTitleTwo] = React.useState("");
  const [selectedMemberSetServiceArea, setSelectedMemberSetServiceArea] = React.useState<AccessReportsServiceArea>(null);
  const [template, setTemplate] = React.useState<AccessReportsWizardTemplate>(null);

  React.useEffect(() => {
    if (!!props.template) {
      extractProductDefaults();
    }
  }, [props.template]);

  const checkReportTitleValidity = (reportTitle: string): boolean => {
    // returns whether a report title string contains the following "invalid" characters:  / \ : . * ? " < > |
    return !/[/\\:.*\\?"<>|]/.test(reportTitle);
  }

  const getSpecialtyValidity = (accessCriteria: AccessReportsWizardAccessCriteria): boolean => {
    // only numbers are considered to be valid for standards
    for (const standard of accessCriteria.standards) {
      for (const value of Object.values(standard)) {
        if (!value || value?.toString() === "0" || value?.toString() === ".") {
          return false;
        }
      }
    }

    // each specialty must have a valid provider set id attached to it
    if (!accessCriteria.providerSet ||
      accessCriteria.providerSet.providerSetId === null || accessCriteria.providerSet.providerSetId === undefined
    ) {
      return false
    }

    return true;
  }

  const onAccessAnalysisDefaultChange = (optionId: number, accessAnalysis: AccessReportsAccessAnalysisElementEnum): void => {
    const newTemplate: AccessReportsWizardTemplate = cloneDeep(template);
    const element: AccessReportsWizardElement = getElementByIdFromTemplate(newTemplate, AccessReportsWizardStepEnum.AccessAnalysis, accessAnalysis);

    if (!!element?.options) {
      for (const option of element.options) {
        if (option.id === optionId && !option.defaultProducts.includes(product.id)) {
          option.defaultProducts.push(product.id);
        } else {
          option.defaultProducts = option.defaultProducts.filter(x => x !== product.id);
        }
      }

      setTemplate(newTemplate);
    }
  }

  const onAccessAnalysisHideChange = (optionId: number, accessAnalysis: AccessReportsAccessAnalysisElementEnum): void => {
    switch (accessAnalysis) {
      case AccessReportsAccessAnalysisElementEnum.CalculationMethod:
        let newHiddenCalculationMethods: number[] = cloneDeep(hiddenCalculationMethods);

        if (newHiddenCalculationMethods.includes(optionId)) {
          newHiddenCalculationMethods = newHiddenCalculationMethods.filter(x => x !== optionId);
        } else {
          newHiddenCalculationMethods.push(optionId);
        }

        setHiddenCalculationMethods(newHiddenCalculationMethods);
        break;
      case AccessReportsAccessAnalysisElementEnum.DistanceMethod:
        let newHiddenDistanceMethods: number[] = cloneDeep(hiddenDistanceMethods);

        if (newHiddenDistanceMethods.includes(optionId)) {
          newHiddenDistanceMethods = newHiddenDistanceMethods.filter(x => x !== optionId);
        } else {
          newHiddenDistanceMethods.push(optionId);
        }

        setHiddenDistanceMethods(newHiddenDistanceMethods);
        break;
      case AccessReportsAccessAnalysisElementEnum.AccessStandard:
        let newHiddenAccessStandards: number[] = cloneDeep(hiddenAccessStandards);

        if (newHiddenAccessStandards.includes(optionId)) {
          newHiddenAccessStandards = newHiddenAccessStandards.filter(x => x !== optionId);
        } else {
          newHiddenAccessStandards.push(optionId);
        }

        setHiddenAccessStandards(newHiddenAccessStandards);
        break;
      default:
        break;
    }
  }

  const onAddGroupClick = (group: string): void => {
    const newGroups: string[] = cloneDeep(groups);
    newGroups.push(group);

    setGroups(newGroups);
  }

  const createByProductDefault = (
    pageFormGroup: AccessReportsWizardFormGroup,
    selected: boolean,
    productId: number,
  ): ByProductDefault => {
    const byProductDefault: ByProductDefault = {
      id: pageFormGroup.byProductDefaults.length > 0 ? Math.max(...pageFormGroup.byProductDefaults.map((x) => x.id)) + 1 : 0,
      productId,
    };

    let chartColorIds: AccessSummaryEditChartColorsElementEnum[] | AccessOverviewEditChartColorsElementEnum[] = [];
    const setPage: ByProductDefault = pageFormGroup.byProductDefaults.find(x => x.productId === productId);

    switch (pageFormGroup.groupPropertyId) {
      case AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardSummaryPages:
        chartColorIds = Object.values(AccessSummaryEditChartColorsElementEnum);
        break;
      case AccessReportsWizardGroupPropertyIdEnum.AccessOverviewPages:
        chartColorIds = Object.values(AccessOverviewEditChartColorsElementEnum);
        break;
      default:
        break;
    }

    for (const element of pageFormGroup.elements) {
      if ((chartColorIds as string[]).includes(element.elementId)) {
        // if a page of this type already exists, use those values to populate admin-specific defaults
        if (!!setPage) {
          byProductDefault[element.elementId] = setPage[element.elementId];
        } else {
          // otherwise, use the defaults from the template form group node 
          if (!!(element.defaultValue.toString())) {
            byProductDefault[element.elementId] = element.defaultValue;
          }
        }
      } else {
        switch (element.elementType) {
          case AccessReportsWizardElementTypeEnum.Checkbox:
            if (Object.values(PageSelectionElementIdEnum).includes(element.elementId as PageSelectionElementIdEnum)) {
              byProductDefault[element.elementId] = selected;
            } else {
              byProductDefault[element.elementId] = false;
            }
            break;
          case AccessReportsWizardElementTypeEnum.ColorPicker:
          case AccessReportsWizardElementTypeEnum.DatePicker:
          case AccessReportsWizardElementTypeEnum.Dropdown:
          case AccessReportsWizardElementTypeEnum.Radio:
          case AccessReportsWizardElementTypeEnum.Unknown:
            byProductDefault[element.elementId] = null;
            break;
          case AccessReportsWizardElementTypeEnum.Input:
            byProductDefault[element.elementId] = "";
            break;
          default:
            break;
        }
      }
    }

    return byProductDefault;
  }

  const getNewAccessCriterion = (name: string, id: number, includeStandards: boolean): AccessReportsWizardAccessCriteria => {
    return {
      defaultProducts: [],
      displayName: name,
      filterMemberSetByOutsideServiceArea: false,
      filterMemberSetByServiceArea: false,
      filterProviderSetByOutsideServiceArea: false,
      filterProviderSetByServiceArea: false,
      groups: [],
      hidden: false,
      hiddenProducts: [],
      id,
      memberSet: {
        employeeGroupStatus: EmployeeGroupStatusEnum.Unknown,
        fileName: "",
        isCensus: false,
        memberSetId: 0,
        memberSetName: "",
        memberSetVersionId: 0,
      },
      memberSetServiceArea: null,
      name,
      providerSet: null,
      providerSetServiceArea: null,
      selected: false,
      standards: includeStandards ? Object.values(AccessReportsWizardNetworkStandardTypeEnum).map((x) => {
        return getAccessStandardDefaultValues(x);
      }) : [],
    };
  }

  const onAddPageClick = (groupPropertyId: AccessReportsWizardGroupPropertyIdEnum): void => {
    const newTemplate: AccessReportsWizardTemplate = cloneDeep(template);
    const pageFormGroup: AccessReportsWizardFormGroup = getFormGroupByGroupPropertyIdFromTemplate(newTemplate, AccessReportsWizardStepEnum.PageOptions, groupPropertyId);

    if (!!pageFormGroup) {
      let selected = false;
      const firstByProductDefault: ByProductDefault = pageFormGroup.byProductDefaults.find(x => x.productId === product.id);

      if (!!firstByProductDefault) {
        for (const property of Object.keys(firstByProductDefault)) {
          if (Object.values(PageSelectionElementIdEnum).includes(property as PageSelectionElementIdEnum)) {
            selected = firstByProductDefault[property] as boolean;
            break;
          }
        }
      }

      const byProductDefault: ByProductDefault = createByProductDefault(pageFormGroup, selected, product.id);

      pageFormGroup.byProductDefaults.push(byProductDefault);

      setTemplate(newTemplate);
    }
  }

  const onAddSpecialtiesClick = (specialties: string[]): void => {
    const newProduct: AccessReportsWizardProduct = cloneDeep(product);

    for (const specialty of specialties) {
      let maxId = 0;
      if (!!newProduct && newProduct.accessCriterion.length) {
        maxId = Math.max(...newProduct.accessCriterion.map((o) => o.id)) + 1;
      }

      const accessCriteria: AccessReportsWizardAccessCriteria = getNewAccessCriterion(specialty, maxId, true);

      newProduct.accessCriterion.push(accessCriteria);
    }

    setProduct(newProduct);
  }

  const onAllowEditToggle = (): void => {
    const newProduct: AccessReportsWizardProduct = cloneDeep(product);
    newProduct.editable = !newProduct.editable;

    setProduct(newProduct);
  }

  const onDisclaimerTextChange = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
    const newTemplate: AccessReportsWizardTemplate = cloneDeep(template);
    const pageOptionsFormGroup: AccessReportsWizardFormGroup = getFormGroupByGroupPropertyIdFromTemplate(newTemplate, AccessReportsWizardStepEnum.PageOptions);
    const disclaimerPage: AccessReportsWizardElement = pageOptionsFormGroup?.elements.find(x => x.elementId === AccessReportsPageOptionsElementEnum.LegalDisclaimer);
    const productOption: AccessReportsWizardOption = disclaimerPage?.options?.find(x => x.id === product.id);

    if (!!productOption) {
      productOption.name = event.target.value;

      setTemplate(newTemplate);
    }
  }

  const onFilterMemberSetByOutsideServiceAreaToggle = (): void => {
    setFilterMemberSetByOutsideServiceArea(!filterMemberSetByOutsideServiceArea);
  }

  const onFilterProviderSetByOutsideServiceAreaToggle = (accessCriteriaId: number): void => {
    if (!!product && accessCriteriaId !== null && accessCriteriaId !== undefined) {
      const newProduct: AccessReportsWizardProduct = cloneDeep(product);
      const accessCriteria: AccessReportsWizardAccessCriteria = newProduct.accessCriterion.find(x => x.id === accessCriteriaId);

      if (!!accessCriteria) {
        accessCriteria.filterProviderSetByOutsideServiceArea = !accessCriteria.filterProviderSetByOutsideServiceArea;

        setProduct(newProduct);
      }
    }
  }

  const onGroupToggle = (accessCriteriaId: number, group: string): void => {
    const newProduct: AccessReportsWizardProduct = cloneDeep(product);
    const accessCriteria = newProduct.accessCriterion.find(x => x.id === accessCriteriaId);

    if (accessCriteria) {
      if (accessCriteria.groups.includes(group)) {
        accessCriteria.groups = accessCriteria.groups.filter(x => x !== group);
      } else {
        accessCriteria.groups.push(group);
      }
    }

    setProduct(newProduct);
  }

  const onNetworkStandardChange = (
    accessCriteria: AccessReportsWizardAccessCriteria,
    networkStandardType: AccessReportsWizardNetworkStandardTypeEnum,
    standardKey: "minCount" | "miles" | "minutes",
    event?: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const newProduct: AccessReportsWizardProduct = cloneDeep(product);
    const accessReportsWizardAccessCriteria: AccessReportsWizardAccessCriteria = newProduct.accessCriterion.find((x) => x.id === accessCriteria.id);

    if (!!accessReportsWizardAccessCriteria) {
      const standard: AccessReportsWizardNetworkStandard = accessReportsWizardAccessCriteria.standards.find((x) => x.type === networkStandardType);

      if (!!standard) {
        if (!!event) {
          // on change
          // ensure that only numbers may be input
          const value: number = parseInt(event.target.value);

          if ((!isNaN(value) && event.target.value !== "0") || !event.target.value) {
            (standard)[standardKey] = value;

            setProduct(newProduct);
          }
        } else {
          // on blur
          if (!(standard)[standardKey] || ((standard)[standardKey] as number).toString() === "0") {
            // assign min value
            (standard)[standardKey] = 1;

            setProduct(newProduct);
          }
        }
      }
    }
  }

  const onNetworkStandardMilesChange = (
    accessCriteria: AccessReportsWizardAccessCriteria,
    networkStandardType: AccessReportsWizardNetworkStandardTypeEnum,
    standardKey: "miles",
    event?: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const newProduct: AccessReportsWizardProduct = cloneDeep(product);
    const accessReportsWizardAccessCriteria: AccessReportsWizardAccessCriteria = newProduct.accessCriterion.find((x) => x.id === accessCriteria.id);

    if (!!accessReportsWizardAccessCriteria) {
      const standard: AccessReportsWizardNetworkStandard = accessReportsWizardAccessCriteria.standards.find((x) => x.type === networkStandardType);

      if (!!standard) {
        if (!!event) {
          // on change
          const re = /^([0-9]{0,4}|[0-9]{0,4}\.[0-9]?)$/;

          // ensure that only float numbers may be input
          if (re.test(event.target.value)) {
            let updateValue: string = event.target.value;
            const value: number = parseFloat(event.target.value);

            if (value >= 1) {
              updateValue = updateValue.replace(/^0+/, "");   // remove leading zeros
            }

            if (!isNaN(value) || !event.target.value || event.target.value === ".") {
              (standard)[standardKey] = parseFloat(updateValue);

              setProduct(newProduct);
            }


          }
        } else {
          // on blur
          if (!(standard)[standardKey] ||
            ((standard)[standardKey]).toString() === "0" ||
            ((standard)[standardKey]).toString() === "." ||
            ((standard)[standardKey]).toString() === "0."
          ) {
            // assign min value
            (standard)[standardKey] = .1;

            setProduct(newProduct);
          }
        }
      }
    }
  }

  const onPageOptionToggle = (elementId: string, key: AccessReportsAdminPageOptionsEnum): void => {
    const newTemplate: AccessReportsWizardTemplate = cloneDeep(template);
    const pageOptionsFormGroup: AccessReportsWizardFormGroup = getFormGroupByGroupPropertyIdFromTemplate(newTemplate, AccessReportsWizardStepEnum.PageOptions);
    const pageOption: AccessReportsWizardElement = pageOptionsFormGroup.elements.find(x => x.elementId === elementId);
    const pageOptionProductOption: AccessReportsWizardOption = pageOption?.options.find(x => x.id === product.id);

    if (!!pageOptionProductOption) {
      switch (key) {
        case AccessReportsAdminPageOptionsEnum.Default:
          if (!pageOptionProductOption.defaultProducts?.includes(product.id)) {
            pageOptionProductOption.defaultProducts = [product.id];
          } else {
            pageOptionProductOption.defaultProducts = [];
          }

          break;
        case AccessReportsAdminPageOptionsEnum.Hide:
          if (!pageOptionProductOption.hiddenProducts?.includes(product.id)) {
            pageOptionProductOption.hiddenProducts = [product.id];
          } else {
            pageOptionProductOption.hiddenProducts = [];
          }

          break;
        default:
          break;
      }

      setTemplate(newTemplate);
    }
  }

  const onPageOptionPageChange = (groupPropertyId: AccessReportsWizardGroupPropertyIdEnum, byProductDefaultId: number, elementId: string, value: string | number | boolean): void => {
    const newTemplate: AccessReportsWizardTemplate = cloneDeep(template);
    const newInvalidPageOptionPageElements: Map<string, string> = cloneDeep(invalidPageOptionPageElements);
    const pageFormGroup: AccessReportsWizardFormGroup = getFormGroupByGroupPropertyIdFromTemplate(newTemplate, AccessReportsWizardStepEnum.PageOptions, groupPropertyId);
    const byProductDefault: ByProductDefault = pageFormGroup?.byProductDefaults.find(x => x.id === byProductDefaultId && x.productId === product.id);

    if (!!byProductDefault) {
      byProductDefault[elementId] = value;
    }

    if (groupPropertyId === AccessReportsWizardGroupPropertyIdEnum.AccessRadiusSearch) {
      const getElementValue = (elementId: string): number => {
        return parseFloat(byProductDefault[elementId]?.toString());
      }

      const handleMapSetting = (mapElementId: string, isInvalid: boolean) => {
        const mappingKey: string = byProductDefault.id + mapElementId;
        const alreadyExistingMapping: string = newInvalidPageOptionPageElements.get(mappingKey);

        if (isInvalid && !alreadyExistingMapping) {
          newInvalidPageOptionPageElements.set(mappingKey, "Radii values must be ascending");
        } else if (!!alreadyExistingMapping && !isInvalid) {
          newInvalidPageOptionPageElements.delete(mappingKey);
        }
      }

      const radiiOne: number = getElementValue(AccessRadiusSearchPageElementEnum.RadiiOne);
      const radiiTwo: number = getElementValue(AccessRadiusSearchPageElementEnum.RadiiTwo);
      const radiiThree: number = getElementValue(AccessRadiusSearchPageElementEnum.RadiiThree);
      const radiiFour: number = getElementValue(AccessRadiusSearchPageElementEnum.RadiiFour);
      const radiiFive: number = getElementValue(AccessRadiusSearchPageElementEnum.RadiiFive);

      let radiiOneInvalid = false;
      let radiiTwoInvalid = false;
      let radiiThreeInvalid = false;
      let radiiFourInvalid = false;
      let radiiFiveInvalid = false;

      if (radiiOne >= radiiTwo || radiiOne >= radiiThree || radiiOne >= radiiFour || radiiOne >= radiiFive) {
        radiiOneInvalid = true;
      }

      if (radiiTwo <= radiiOne || radiiTwo >= radiiThree || radiiTwo >= radiiFour || radiiTwo >= radiiFive) {
        radiiTwoInvalid = true;
      }

      if (radiiThree <= radiiOne || radiiThree <= radiiTwo || radiiThree >= radiiFour || radiiThree >= radiiFive) {
        radiiThreeInvalid = true;
      }

      if (radiiFour <= radiiOne || radiiFour <= radiiTwo || radiiFour <= radiiThree || radiiFour >= radiiFive) {
        radiiFourInvalid = true;
      }

      if (radiiFive <= radiiOne || radiiFive <= radiiTwo || radiiFive <= radiiThree || radiiFive <= radiiFour) {
        radiiFiveInvalid = true;
      }

      handleMapSetting(AccessRadiusSearchPageElementEnum.RadiiOne, radiiOneInvalid);
      handleMapSetting(AccessRadiusSearchPageElementEnum.RadiiTwo, radiiTwoInvalid);
      handleMapSetting(AccessRadiusSearchPageElementEnum.RadiiThree, radiiThreeInvalid);
      handleMapSetting(AccessRadiusSearchPageElementEnum.RadiiFour, radiiFourInvalid);
      handleMapSetting(AccessRadiusSearchPageElementEnum.RadiiFive, radiiFiveInvalid);
    }

    setInvalidPageOptionPageElements(newInvalidPageOptionPageElements);
    setTemplate(newTemplate);
  }

  const onPageOptionPageToggle = (groupPropertyId: AccessReportsWizardGroupPropertyIdEnum, key: AccessReportsAdminPageOptionsEnum,): void => {
    const newTemplate: AccessReportsWizardTemplate = cloneDeep(template);
    const pageFormGroup: AccessReportsWizardFormGroup = getFormGroupByGroupPropertyIdFromTemplate(newTemplate, AccessReportsWizardStepEnum.PageOptions, groupPropertyId);

    switch (key) {
      case AccessReportsAdminPageOptionsEnum.Default:
        const byProductDefaults: ByProductDefault[] = pageFormGroup.byProductDefaults.filter(x => x.productId === product.id);

        for (const byProductDefault of byProductDefaults) {
          const selectionKey = Object.keys(byProductDefault).find(x => Object.values(PageSelectionElementIdEnum).includes(x as PageSelectionElementIdEnum))
          byProductDefault[selectionKey] = !byProductDefault[selectionKey] as boolean;
        }

        break;
      case AccessReportsAdminPageOptionsEnum.Hide:
        if (pageFormGroup.hiddenProducts.includes(product.id)) {
          pageFormGroup.hiddenProducts = pageFormGroup.hiddenProducts.filter(x => x !== product.id);
        } else {
          pageFormGroup.hiddenProducts.push(product.id);
        }

        break;
      case AccessReportsAdminPageOptionsEnum.AllowMultiple:
        if (pageFormGroup.disallowedMultipleProducts.includes(product.id)) {
          pageFormGroup.disallowedMultipleProducts = pageFormGroup.disallowedMultipleProducts.filter(x => x !== product.id);
        } else {
          pageFormGroup.disallowedMultipleProducts.push(product.id);
        }

        break;
      default:
        break;
    }

    setTemplate(newTemplate);
  }

  const onProductInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const newProduct: AccessReportsWizardProduct = cloneDeep(product);
    newProduct.displayName = event.target.value;
    newProduct.name = event.target.value;

    setProduct(newProduct);
  }

  const onProductTypeChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
    const newTemplate: AccessReportsWizardTemplate = cloneDeep(props.template);

    if (!!newTemplate) {
      const coverPage: AccessReportsWizardPage = newTemplate.pages.find(x => x.title === AccessReportsWizardStepEnum.CoverPage);

      if (!!coverPage) {
        const coverPageElements: AccessReportsWizardElement[] = flatten(coverPage.formGroups.map(x => x.elements));
        const productTypeElement: AccessReportsWizardElement = coverPageElements.find(x => x.elementId === AccessReportsCoverPageElementEnum.ProductType);

        if (!!productTypeElement && !!productTypeElement.options) {
          const newProductType: AccessReportsWizardOption = productTypeElement.options.find(x => x.id.toString() === event.target.value);

          setProductType(newProductType);
        }
      }
    }
  }

  const onProviderGroupChange = (accessCriteria: AccessReportsWizardAccessCriteria, providerSetModel: AccessReportProviderSetModel): void => {
    const newProduct: AccessReportsWizardProduct = cloneDeep(product);
    const accessReportsWizardAccessCriteria: AccessReportsWizardAccessCriteria = newProduct.accessCriterion.find((x) => x.id === accessCriteria.id);

    if (!!accessReportsWizardAccessCriteria) {
      const providerSet: AccessReportsWizardProviderSet = {
        providerSetId: providerSetModel.providerSetId,
        providerSetName: providerSetModel.name,
        providerSetVersionId: providerSetModel.versions[0]?.providerSetVersionId,
      }

      accessReportsWizardAccessCriteria.providerSet = providerSet;

      setProduct(newProduct);
    }
  }

  const onReportTitleInputChange = (reportTitleElement: AccessReportsCoverPageElementEnum, event: React.ChangeEvent<HTMLInputElement>): void => {
    const newInvalidElements: Map<string, string> = invalidElements;

    const inputsWithValidation: AccessReportsCoverPageElementEnum[] = [
      AccessReportsCoverPageElementEnum.ReportTitleOne,
      AccessReportsCoverPageElementEnum.ReportTitleTwo,
      AccessReportsCoverPageElementEnum.ReportTitleThree,
    ];

    if (inputsWithValidation.includes(reportTitleElement)) {
      const invalidReportTitleString = `Your report title may not contain any of the following characters: \u002f \u005c \u003a \u002e \u002a \u003f \u0022 \u003c \u003e |`;
      const alreadyExistingMapping: string = newInvalidElements.get(reportTitleElement);
      const isValid: boolean = checkReportTitleValidity(event.target.value);

      if (isValid) {
        if (!!alreadyExistingMapping) {
          newInvalidElements.delete(reportTitleElement);
        }
      } else {
        if (!alreadyExistingMapping) {
          newInvalidElements.set(reportTitleElement, invalidReportTitleString);
        }
      }
    }

    switch (reportTitleElement) {
      case AccessReportsCoverPageElementEnum.ReportTitleOne:
        setInvalidElements(newInvalidElements);
        setReportTitleOne(event.target.value);
        break;
      case AccessReportsCoverPageElementEnum.ReportTitleTwo:
        setInvalidElements(newInvalidElements);
        setReportTitleTwo(event.target.value);
        break;
      case AccessReportsCoverPageElementEnum.ReportTitleThree:
        setInvalidElements(newInvalidElements);
        setReportTitleThree(event.target.value);
        break;
      default:
        break;
    }
  }

  const onRemoveFormGroupClick = (groupPropertyId: AccessReportsWizardGroupPropertyIdEnum, byProductDefaultId: number): void => {
    const newTemplate: AccessReportsWizardTemplate = cloneDeep(template);
    const pageFormGroup: AccessReportsWizardFormGroup = getFormGroupByGroupPropertyIdFromTemplate(newTemplate, AccessReportsWizardStepEnum.PageOptions, groupPropertyId);

    if (!!pageFormGroup) {
      pageFormGroup.byProductDefaults = pageFormGroup.byProductDefaults.filter(x => x.id !== byProductDefaultId);
    }

    setTemplate(newTemplate);
  }

  const onRemoveGroupClick = (group: string): void => {
    const groupsClone: string[] = cloneDeep(groups);
    const newGroups: string[] = groupsClone.filter(x => x !== group);
    const newProduct: AccessReportsWizardProduct = cloneDeep(product);

    // if editing a product, remove the group from every access criteria it may have already been applied to
    for (const accessCriteria of newProduct.accessCriterion) {
      if (accessCriteria.groups) {
        if (accessCriteria.groups.includes(group)) {
          accessCriteria.groups = accessCriteria.groups.filter(x => x !== group);
        }
      }
    }

    setGroups(newGroups);
    setProduct(newProduct);
  }

  const onRemoveSpecialtyClick = (accessCriteria: AccessReportsWizardAccessCriteria): void => {
    const newProduct: AccessReportsWizardProduct = cloneDeep(product);
    newProduct.accessCriterion = newProduct.accessCriterion.filter(x => x.id !== accessCriteria.id);

    // adjust the id values so there are no gaps
    for (let i = 0; i < newProduct.accessCriterion.length; i++) {
      newProduct.accessCriterion[i].id = i;
    }

    // prevent the next closest specialty accordion panel from automatically opening
    $('#access-reports-admin__specialties--accordion .collapse').attr("data-bs-parent", "#access-reports-admin__specialties--accordion").collapse('hide');

    setProduct(newProduct);
  }

  const onSaveAccessOverviewChartColorsClick = (byProductDefault: ByProductDefault): void => {
    const newTemplate: AccessReportsWizardTemplate = cloneDeep(template);
    const pageFormGroup: AccessReportsWizardFormGroup = getFormGroupByGroupPropertyIdFromTemplate(newTemplate, AccessReportsWizardStepEnum.PageOptions, AccessReportsWizardGroupPropertyIdEnum.AccessOverviewPages);

    if (!!pageFormGroup) {
      const byProductDefaults: ByProductDefault[] = pageFormGroup?.byProductDefaults.filter(x => x.productId === product.id);

      for (const bpd of byProductDefaults) {
        for (const key of Object.keys(byProductDefault)) {
          if (Object.values(AccessOverviewEditChartColorsElementEnum).includes(key as AccessOverviewEditChartColorsElementEnum)) {
            bpd[key] = byProductDefault[key];
          }
        }
      }

      setTemplate(newTemplate);
    }
  }

  const onSaveAccessSummaryChartColorsClick = (byProductDefault: ByProductDefault): void => {
    const newTemplate: AccessReportsWizardTemplate = cloneDeep(template);
    const pageFormGroup: AccessReportsWizardFormGroup = getFormGroupByGroupPropertyIdFromTemplate(newTemplate, AccessReportsWizardStepEnum.PageOptions, AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardSummaryPages);

    if (!!pageFormGroup) {
      const byProductDefaults: ByProductDefault[] = pageFormGroup?.byProductDefaults.filter(x => x.productId === product.id);

      for (const bpd of byProductDefaults) {
        for (const key of Object.keys(byProductDefault)) {
          if (Object.values(AccessSummaryEditChartColorsElementEnum).includes(key as AccessSummaryEditChartColorsElementEnum)) {
            bpd[key] = byProductDefault[key];
          }
        }
      }

      setTemplate(newTemplate);
    }
  }

  const onSaveColumnsClick = (groupPropertyId: AccessReportsWizardGroupPropertyIdEnum, columnDefaults: ColumnDefaults): void => {
    const newTemplate: AccessReportsWizardTemplate = cloneDeep(template);
    const pageFormGroup: AccessReportsWizardFormGroup = getFormGroupByGroupPropertyIdFromTemplate(newTemplate, AccessReportsWizardStepEnum.PageOptions, groupPropertyId);

    if (!!pageFormGroup) {
      const columnDefault: ColumnDefaults = pageFormGroup.columnDefaults.find(x => x.productId === product.id);

      if (!!columnDefault) {
        const index: number = pageFormGroup.columnDefaults.indexOf(columnDefault);
        pageFormGroup.columnDefaults[index] = columnDefaults;
      } else {
        pageFormGroup.columnDefaults.push(columnDefaults);
      }

      setTemplate(newTemplate);
    }
  }

  const onSaveProductClick = (): void => {
    const newTemplate: AccessReportsWizardTemplate = template;

    if (!!newTemplate) {
      const accessAnalysisPage: AccessReportsWizardPage = newTemplate.pages.find(x => x.title === AccessReportsWizardStepEnum.AccessAnalysis);
      const coverPage: AccessReportsWizardPage = newTemplate.pages.find(x => x.title === AccessReportsWizardStepEnum.CoverPage);
      const newProduct: AccessReportsWizardProduct = product;

      if (!!accessAnalysisPage && !newProduct.skipAccessAnalysis) {
        const accessAnalysisPageElements: AccessReportsWizardElement[] = flatten(accessAnalysisPage.formGroups.map(x => x.elements));

        for (const element of accessAnalysisPageElements) {
          switch (element.elementId) {
            case AccessReportsAccessAnalysisElementEnum.CalculationMethod:
              for (const option of element.options) {
                if (option.hiddenProducts.includes(newProduct.id)) {
                  // if this calculation method option already includes the product from a previous save,
                  // only remove the product from the array if the current ui model does not
                  // have the option marked as hidden now
                  if (!hiddenCalculationMethods.includes(option.id)) {
                    option.hiddenProducts = option.hiddenProducts.filter(x => x !== newProduct.id);
                  }
                } else {
                  // if this calculation method option does not already include the product,
                  // add it if the ui as flagged it to be hidden now
                  if (hiddenCalculationMethods.includes(option.id)) {
                    option.hiddenProducts.push(newProduct.id);
                  }
                }
              }

              break;
            case AccessReportsAccessAnalysisElementEnum.DistanceMethod:
              for (const option of element.options) {
                if (option.hiddenProducts.includes(newProduct.id)) {
                  // if this distance method option already includes the product from a previous save,
                  // only remove the product from the array if the current ui model does not
                  // have the option marked as hidden now
                  if (!hiddenDistanceMethods.includes(option.id)) {
                    option.hiddenProducts = option.hiddenProducts.filter(x => x !== newProduct.id);
                  }
                } else {
                  // if this distance method option does not already include the product,
                  // add it if the ui as flagged it to be hidden now
                  if (hiddenDistanceMethods.includes(option.id)) {
                    option.hiddenProducts.push(newProduct.id);
                  }
                }
              }

              break;
            case AccessReportsAccessAnalysisElementEnum.AccessStandard:
              for (const option of element.options) {
                if (option.hiddenProducts.includes(newProduct.id)) {
                  // if this access standard option already includes the product from a previous save,
                  // only remove the product from the array if the current ui model does not
                  // have the option marked as hidden now
                  if (!hiddenAccessStandards.includes(option.id)) {
                    option.hiddenProducts = option.hiddenProducts.filter(x => x !== newProduct.id);
                  }
                } else {
                  // if this access standard option does not already include the product,
                  // add it if the ui as flagged it to be hidden now
                  if (hiddenAccessStandards.includes(option.id)) {
                    option.hiddenProducts.push(newProduct.id);
                  }
                }
              }

              break;
            default:
              break;
          }
        }
      }

      if (!!coverPage) {
        // for string inputs, defaults at a per-product level will be stored in options,
        // with each option being a different product and the name/display name being the input values
        const coverPageElements: AccessReportsWizardElement[] = flatten(coverPage.formGroups.map(x => x.elements));

        for (const element of coverPageElements) {
          switch (element.elementId) {
            case AccessReportsCoverPageElementEnum.Product:
              newProduct.previousElementFilterId = productType.id;
              newProduct.groups = groups;

              if (!!newProduct.accessCriterion && !newProduct.skipAccessAnalysis) {
                for (const accessCriteria of newProduct.accessCriterion) {
                  accessCriteria.filterMemberSetByOutsideServiceArea = filterMemberSetByOutsideServiceArea;
                  accessCriteria.memberSetServiceArea = selectedMemberSetServiceArea;

                  if (!!selectedMemberSetServiceArea &&
                    selectedMemberSetServiceArea.serviceAreaId !== null &&
                    selectedMemberSetServiceArea.serviceAreaId !== undefined
                  ) {
                    accessCriteria.filterMemberSetByServiceArea = true;
                  } else {
                    accessCriteria.filterMemberSetByServiceArea = false;
                  }
                }
              }

              const existingProduct: AccessReportsWizardOption = element.options.find(x => x.id === newProduct.id);

              if (!!existingProduct) {
                const productIndex: number = element.options.indexOf(existingProduct);
                element.options[productIndex] = newProduct;
              } else {
                element.options.push(newProduct);
              }

              break;
            case AccessReportsCoverPageElementEnum.ReportTitleOne:
              const reportTitleOneOption: AccessReportsWizardOption = element.options.find(x => x.id === newProduct.id);

              if (!!reportTitleOneOption) {
                reportTitleOneOption.name = reportTitleOne;
                reportTitleOneOption.displayName = reportTitleOne;
              } else {
                const newOption: AccessReportsWizardOption = {
                  defaultProducts: [],
                  displayName: reportTitleOne,
                  hiddenProducts: [],
                  id: newProduct.id,
                  name: reportTitleOne,
                };

                element.options.push(newOption);
              }

              break;
            case AccessReportsCoverPageElementEnum.ReportTitleTwo:
              const reportTitleTwoOption: AccessReportsWizardOption = element.options.find(x => x.id === newProduct.id);

              if (!!reportTitleTwoOption) {
                reportTitleTwoOption.name = reportTitleTwo;
                reportTitleTwoOption.displayName = reportTitleTwo;
              } else {
                const newOption: AccessReportsWizardOption = {
                  defaultProducts: [],
                  displayName: reportTitleTwo,
                  hiddenProducts: [],
                  id: newProduct.id,
                  name: reportTitleTwo,
                };

                element.options.push(newOption);
              }

              break;
            case AccessReportsCoverPageElementEnum.ReportTitleThree:
              const reportTitleThreeOption: AccessReportsWizardOption = element.options.find(x => x.id === newProduct.id);

              if (!!reportTitleThreeOption) {
                reportTitleThreeOption.name = reportTitleThree;
                reportTitleThreeOption.displayName = reportTitleThree;
              } else {
                const newOption: AccessReportsWizardOption = {
                  defaultProducts: [],
                  displayName: reportTitleThree,
                  hiddenProducts: [],
                  id: newProduct.id,
                  name: reportTitleThree,
                };

                element.options.push(newOption);
              }

              break;
            default:
              break;
          }
        }
      }

      props.onSaveProductClick(newTemplate);

      // redirect to dashboard
      history.push(`/access-reports/admin/dashboard`);
    }
  }

  const onSaveSpecialtiesClick = (specialties: AccessReportsWizardOption[]): void => {
    const newProduct: AccessReportsWizardProduct = { ...product };
    newProduct.accessCriterion = specialties as AccessReportsWizardAccessCriteria[];

    setProduct(newProduct);
  }

  const onMemberSetServiceAreaDropdownChange = (newSelectedMemberSetServiceArea: AccessReportsServiceArea): void => {
    setSelectedMemberSetServiceArea(newSelectedMemberSetServiceArea);
  }

  const onProviderSetServiceAreaDropdownChange = (newProduct: AccessReportsWizardProduct): void => {
    setProduct(newProduct);
  }

  const onSaveSpecialtyNameClick = (accessCriteriaId: number, accessCriteriaName: string): void => {
    if (!!product) {
      const newProduct: AccessReportsWizardProduct = cloneDeep(product);
      const accessCriteria: AccessReportsWizardAccessCriteria = newProduct.accessCriterion.find(x => x.id === accessCriteriaId);

      if (!!accessCriteria) {
        accessCriteria.name = accessCriteriaName;

        setProduct(newProduct);
      }
    }
  }

  const onSkipAccessAnalysisToggle = (): void => {
    let newTemplate: AccessReportsWizardTemplate = cloneDeep(template);
    const newProduct: AccessReportsWizardProduct = cloneDeep(product);
    newProduct.skipAccessAnalysis = !newProduct.skipAccessAnalysis;
    let newGroups: string[] = groups;
    let newHiddenAccessStandards: number[] = hiddenAccessStandards;
    let newHiddenCalculationMethods: number[] = hiddenCalculationMethods;
    let newHiddenDistanceMethods: number[] = hiddenDistanceMethods;

    const handleSplitOptions = (): void => {
      const pageOptionsFormGroup: AccessReportsWizardFormGroup = getFormGroupByGroupPropertyIdFromTemplate(newTemplate, AccessReportsWizardStepEnum.PageOptions);
      const splitOptions: AccessReportsPageOptionsElementEnum[] = [AccessReportsPageOptionsElementEnum.SplitEmployeeGroupByStandard, AccessReportsPageOptionsElementEnum.SplitPagesBySpecialty];

      for (const split of splitOptions) {
        const splitElement = pageOptionsFormGroup.elements.find(x => x.elementId === split);
        const splitOption: AccessReportsWizardOption = splitElement?.options.find(x => x.id === product.id);

        if (newProduct.skipAccessAnalysis) {
          // if skipping access analysis, remove defaulting the split option
          // and add the product to the list of hidden products for the split options
          splitOption.defaultProducts = splitOption.defaultProducts.filter(x => x !== product.id);

          if (!splitOption.hiddenProducts.includes(product.id)) {
            splitOption.hiddenProducts.push(newProduct.id);
          }
        } else {
          // if the access analysis is not being skipped,
          // remove the product from the list of hidden products for the split option
          splitOption.hiddenProducts = splitOption.hiddenProducts.filter(x => x !== product.id);
        }
      }
    }

    const handlePages = (): void => {
      const groupPropertyIds = Object.values(AccessReportsWizardGroupPropertyIdEnum).filter(x => x !== AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardProviderCountPages);

      for (const groupPropertyId of groupPropertyIds) {
        const pageFormGroup: AccessReportsWizardFormGroup = getFormGroupByGroupPropertyIdFromTemplate(newTemplate, AccessReportsWizardStepEnum.PageOptions, groupPropertyId);

        if (newProduct.skipAccessAnalysis) {
          // if skipping access analysis, remove the product from the by-product defaults, column defaults, and multiple products
          // while also making sure to hide the page for this product
          pageFormGroup.byProductDefaults = pageFormGroup?.byProductDefaults.filter(x => x.productId !== newProduct.id);
          pageFormGroup.columnDefaults = pageFormGroup?.columnDefaults.filter(x => x.productId !== newProduct.id);
          pageFormGroup.disallowedMultipleProducts = pageFormGroup.disallowedMultipleProducts.filter(x => x !== newProduct.id);

          if (!pageFormGroup.hiddenProducts.includes(newProduct.id)) {
            pageFormGroup.hiddenProducts.push(newProduct.id);
          }
        } else {
          // if the access analysis is not being skipped,
          // remove the product from the list of hidden products for the page
          pageFormGroup.hiddenProducts = pageFormGroup.hiddenProducts.filter(x => x !== newProduct.id);
        }
      }
    }

    if (newProduct.skipAccessAnalysis) {
      // whenever is access analysis required is checked:
      // 1.) create singular fake access criterion that will carry the provider set information
      // 2.) clear groups and access analysis page dropdowns defaults, as there are no real access criterion to assign to groups
      // 3.) hide and remove defaults from the page options skips
      // 4.) remove all non-provider count pages and page-speciic properties from being stored in the template to conserve space
      // 5.) create a base for provider count page
      newProduct.accessCriterion = [getNewAccessCriterion("", 0, false)];
      newProduct.groups = [];
      newGroups = [];

      const accessAnalysisPage: AccessReportsWizardPage = newTemplate.pages.find(x => x.title === AccessReportsWizardStepEnum.AccessAnalysis);

      if (!!accessAnalysisPage) {
        const accessAnalysisPageElements: AccessReportsWizardElement[] = flatten(accessAnalysisPage.formGroups.map(x => x.elements));

        for (const element of accessAnalysisPageElements) {
          for (const option of element.options) {
            option.hiddenProducts = option.hiddenProducts.filter(x => x !== newProduct.id);
            option.defaultProducts = option.defaultProducts.filter(x => x !== newProduct.id);
          }
        }
      }

      newHiddenAccessStandards = newHiddenAccessStandards.filter(x => x !== newProduct.id);
      newHiddenCalculationMethods = newHiddenCalculationMethods.filter(x => x !== newProduct.id);
      newHiddenDistanceMethods = newHiddenDistanceMethods.filter(x => x !== newProduct.id);

      handleSplitOptions();
      handlePages();

      newTemplate = buildNewPagesForProduct(newTemplate, newProduct.id, true);
    } else {
      // whenever is access analysis required is unchecked:
      // 1.) clear access criterion, as unchecking means that the array only consisted of a fake one
      // 2.) create a base for all pages and page-specific properties
      // 3.) unhide the split page options
      newProduct.accessCriterion = [];
      newTemplate = buildNewPagesForProduct(newTemplate, newProduct.id, false);

      handleSplitOptions();
      handlePages();
    }

    setGroups(newGroups);
    setHiddenAccessStandards(newHiddenAccessStandards);
    setHiddenCalculationMethods(newHiddenCalculationMethods);
    setHiddenDistanceMethods(newHiddenDistanceMethods);
    setProduct(newProduct);
    setTemplate(newTemplate);
  }

  const buildNewPagesForProduct = (template: AccessReportsWizardTemplate, productId: number, skipAccessAnalysis: boolean): AccessReportsWizardTemplate => {
    // if skipping the access analysis, the only "page" we want to display is the provider count page
    const groupPropertyIds: AccessReportsWizardGroupPropertyIdEnum[] = skipAccessAnalysis ?
      [AccessReportsWizardGroupPropertyIdEnum.AccessReportsWizardProviderCountPages] :
      Object.values(AccessReportsWizardGroupPropertyIdEnum);

    for (const groupPropertyId of groupPropertyIds) {
      const pageFormGroup: AccessReportsWizardFormGroup = getFormGroupByGroupPropertyIdFromTemplate(template, AccessReportsWizardStepEnum.PageOptions, groupPropertyId);
      const byProductDefault: ByProductDefault = pageFormGroup?.byProductDefaults.find(x => x.productId === productId);
      // each page type should at least have the option to add a page or modify
      if (!byProductDefault) {
        const newByProductDefault: ByProductDefault = createByProductDefault(pageFormGroup, false, productId);
        pageFormGroup.byProductDefaults.push(newByProductDefault);
      }

      const productColumnDefaults: ColumnDefaults = pageFormGroup?.columnDefaults.find(x => x.productId === productId);

      if (!productColumnDefaults) {
        const genericColumnDefaults: ColumnDefaults = pageFormGroup?.columnDefaults.find(x => isNil(x.productId));
        const columnDefaults: ColumnDefaults = {
          productId,
          selectedColumns: !!genericColumnDefaults ? cloneDeep(genericColumnDefaults.selectedColumns) : [],
        };

        pageFormGroup.columnDefaults.push(columnDefaults);
      }
    }

    return template;
  }

  const setWizardStep = (newPage: number): void => {
    history.push(`/access-reports/admin/product/${product?.id}/step/${newPage + 1}`);
  }

  const createWizardStep = (
    wizardStep: AccessReportsAdminAddProductStepEnum,
    currentWizardStep: number,
    nextPath?: string,
    onSaveProductClick?: () => void,
    previousPath?: string,
  ): React.ReactNode => {
    let nextPathDisabled = true;
    let nextPathDisabledTooltipText = "Error";
    let redirect = false;
    let step: React.ReactNode = null;

    if (!props.template || !product) {
      step = (
        <div className="row">
          <div className="col-12">
            <div className="text-center">
              <LoadingSpinner />
            </div>
          </div>
        </div>
      );
    } else {
      // at least one option must be defaulted for each access analysis type
      let calculationMethodValid = false;
      let distanceMethodValid = false;
      let accessStandardValid = false;
      let calculationMethodHasDefault = false;
      let distanceMethodHasDefault = false;
      let accessStandardHasDefault = false;

      if (!product.skipAccessAnalysis) {
        const accessAnalysisPageElements: AccessReportsWizardElement[] = getAllElementsByPageFromTemplate(template, AccessReportsWizardStepEnum.AccessAnalysis);

        for (const element of accessAnalysisPageElements) {
          const defaultProductsByOption = element.options.map(x => x.defaultProducts);
          let hasDefault = false;
          for (const defaultProducts of defaultProductsByOption) {
            if (defaultProducts.includes(product?.id)) {
              hasDefault = true;
              break;
            }
          }

          if (hasDefault) {
            // do not allow the admin to hide all options from the end user
            switch (element.elementId) {
              case AccessReportsAccessAnalysisElementEnum.CalculationMethod:
                calculationMethodValid = hiddenCalculationMethods.length < element.options.length;
                calculationMethodHasDefault = hasDefault;
                break;
              case AccessReportsAccessAnalysisElementEnum.DistanceMethod:
                distanceMethodValid = hiddenDistanceMethods.length < element.options.length;
                distanceMethodHasDefault = hasDefault;
                break;
              case AccessReportsAccessAnalysisElementEnum.AccessStandard:
                accessStandardValid = hiddenAccessStandards.length < element.options.length;
                accessStandardHasDefault = hasDefault;
                break;
              default:
                break;
            }
          }
        }
      }

      const invalidSpecialty: AccessReportsWizardAccessCriteria = product?.accessCriterion.find(x => !getSpecialtyValidity(x))
      switch (wizardStep) {
        case AccessReportsAdminAddProductStepEnum.ProductDetails:
          if (
            !!product &&
            !!product.displayName &&
            !!product.name &&
            !!productType &&
            (product.skipAccessAnalysis || (!product.skipAccessAnalysis && groups.length > 0)) &&
            (product.skipAccessAnalysis || (!product.skipAccessAnalysis && calculationMethodValid)) &&
            (product.skipAccessAnalysis || (!product.skipAccessAnalysis && distanceMethodValid)) &&
            (product.skipAccessAnalysis || (!product.skipAccessAnalysis && accessStandardValid)) &&
            invalidElements.size === 0
          ) {
            nextPathDisabled = false;
          }

          if (!product?.skipAccessAnalysis) {
            if (!groups || groups.length === 0) {
              nextPathDisabledTooltipText = "Please input at least one group";
            }

            if (!accessStandardHasDefault) {
              nextPathDisabledTooltipText = "Please select a default access standard"
            }

            if (!accessStandardValid) {
              nextPathDisabledTooltipText = "Please do not hide all access standard options";
            }

            if (!distanceMethodHasDefault) {
              nextPathDisabledTooltipText = "Please select a default distance method"
            }

            if (!distanceMethodValid) {
              nextPathDisabledTooltipText = "Please do not hide all distance method options";
            }

            if (!calculationMethodHasDefault) {
              nextPathDisabledTooltipText = "Please select a default calculation method"
            }

            if (!calculationMethodValid) {
              nextPathDisabledTooltipText = "Please do not hide all calculaton method options";
            }
          }

          if (invalidElements.size > 0) {
            nextPathDisabledTooltipText = "Please correct the fields with invalid values";
          }

          if (!product.displayName || !product.name) {
            nextPathDisabledTooltipText = "Please input a product name";
          }

          if (!productType) {
            nextPathDisabledTooltipText = "Please input a product type";
          }

          step = (
            <AccessReportsAdminAddProductDetails
              disableUserActions={props.disableUserActions}
              groups={groups}
              hiddenCalculationMethods={hiddenCalculationMethods}
              hiddenDistanceMethods={hiddenDistanceMethods}
              hiddenAccessStandards={hiddenAccessStandards}
              invalidElements={invalidElements}
              onAccessAnalysisDefaultChange={onAccessAnalysisDefaultChange}
              onAccessAnalysisHideChange={onAccessAnalysisHideChange}
              onAddGroupClick={onAddGroupClick}
              onAllowEditToggle={onAllowEditToggle}
              onProductInputChange={onProductInputChange}
              onProductTypeChange={onProductTypeChange}
              onRemoveGroupClick={onRemoveGroupClick}
              onReportTitleInputChange={onReportTitleInputChange}
              onSkipAccessAnalysisToggle={onSkipAccessAnalysisToggle}
              product={product}
              productType={productType}
              productTypes={productTypes}
              reportTitleOne={reportTitleOne}
              reportTitleTwo={reportTitleTwo}
              reportTitleThree={reportTitleThree}
              template={template}
            />
          );
          break;
        case AccessReportsAdminAddProductStepEnum.Specialties:
          if (
            !!product &&
            !!product.displayName &&
            !!product.name &&
            !!productType &&
            (product.skipAccessAnalysis || (!product.skipAccessAnalysis && groups.length > 0)) &&
            (product.skipAccessAnalysis || (!product.skipAccessAnalysis && calculationMethodValid)) &&
            (product.skipAccessAnalysis || (!product.skipAccessAnalysis && distanceMethodValid)) &&
            (product.skipAccessAnalysis || (!product.skipAccessAnalysis && accessStandardValid))
          ) {
            if (product.accessCriterion.length > 0 && !invalidSpecialty) {
              nextPathDisabled = false;
            }

            if (invalidSpecialty) {
              nextPathDisabledTooltipText = "Please finish adding all necessary information to each specialty";
            }

            if (product && product.accessCriterion.length < 1) {
              nextPathDisabledTooltipText = "Please add at least one specialty";
            }

            step = (
              <AccessReportsAdminAddProductSpecialties
                disableUserActions={props.disableUserActions}
                filterMemberSetByOutsideServiceArea={filterMemberSetByOutsideServiceArea}
                groups={groups}
                onFilterMemberSetByOutsideServiceAreaToggle={onFilterMemberSetByOutsideServiceAreaToggle}
                onAddSpecialtiesClick={onAddSpecialtiesClick}
                onFilterProviderSetByOutsideServiceAreaToggle={onFilterProviderSetByOutsideServiceAreaToggle}
                onGroupToggle={onGroupToggle}
                onMemberSetServiceAreaDropdownChange={onMemberSetServiceAreaDropdownChange}
                onNetworkStandardChange={onNetworkStandardChange}
                onNetworkStandardMilesChange={onNetworkStandardMilesChange}
                onProviderGroupChange={onProviderGroupChange}
                onProviderSetServiceAreaDropdownChange={onProviderSetServiceAreaDropdownChange}
                onRemoveSpecialtyClick={onRemoveSpecialtyClick}
                onSaveSpecialtiesClick={onSaveSpecialtiesClick}
                onSaveSpecialtyNameClick={onSaveSpecialtyNameClick}
                product={product}
                selectedMemberSetServiceArea={selectedMemberSetServiceArea}
              />
            );
          } else {
            redirect = true;
          }

          break;
        case AccessReportsAdminAddProductStepEnum.PageOptions:
          if (
            !!product &&
            !!product.displayName &&
            !!product.name &&
            !!productType &&
            (product.skipAccessAnalysis || (!product.skipAccessAnalysis && groups.length > 0)) &&
            (product.skipAccessAnalysis || (!product.skipAccessAnalysis && calculationMethodValid)) &&
            (product.skipAccessAnalysis || (!product.skipAccessAnalysis && distanceMethodValid)) &&
            (product.skipAccessAnalysis || (!product.skipAccessAnalysis && accessStandardValid)) &&
            product.accessCriterion.length > 0 &&
            !invalidSpecialty
          ) {
            let radiiOneMissing = false;
            const radiusSearchFormGroup: AccessReportsWizardFormGroup = getFormGroupByGroupPropertyIdFromTemplate(template, AccessReportsWizardStepEnum.PageOptions, AccessReportsWizardGroupPropertyIdEnum.AccessRadiusSearch);
            const radiusByProductDefaults: ByProductDefault[] = radiusSearchFormGroup?.byProductDefaults.filter(x => x.productId === product.id);

            for (const radiusByProductDefault of radiusByProductDefaults) {
              if (!radiusByProductDefault[AccessRadiusSearchPageElementEnum.RadiiOne]) {
                radiiOneMissing = true;
                break;
              }
            }

            const pageOptionsFormGroup: AccessReportsWizardFormGroup = getFormGroupByGroupPropertyIdFromTemplate(template, AccessReportsWizardStepEnum.PageOptions);
            const legalDisclaimerElement: AccessReportsWizardElement = pageOptionsFormGroup?.elements.find(x => x.elementId === AccessReportsPageOptionsElementEnum.LegalDisclaimer);
            const productOption: AccessReportsWizardOption = legalDisclaimerElement?.options.find(x => x.id === product?.id);

            // the legal disclaimer section is considered to be valid if
            // the legal disclaimer has been defaulted and there is text present in the textarea field OR
            // the legal disclaimer has not been defaulted (text present irrelevant)

            // the following only applies if the is access analysis required checkbox is not checked:
            // the access radius search section is considered to be valid if
            // a radius search page form group is not present OR a radius search page form group IS present and
            // the page type is hidden OR it is not hidden and contains at least one radii value AND
            // each radii must be in ascending order
            if (((productOption?.defaultProducts.includes(product?.id) && productOption?.name !== "") ||
              (!productOption?.defaultProducts.includes(product?.id)) &&
              invalidPageOptionPageElements.size === 0) && (product.skipAccessAnalysis || (!product.skipAccessAnalysis && (
                !!radiusSearchFormGroup?.hiddenProducts.includes(product.id) ||
                (!radiusSearchFormGroup?.hiddenProducts.includes(product.id) && !radiiOneMissing)
              )))) {
              nextPathDisabled = false;
            }

            // access radius search page validity
            if (!product.skipAccessAnalysis && invalidPageOptionPageElements.size > 0) {
              nextPathDisabledTooltipText = "Access Search Radius Page radii values must be in ascending order";
            }

            if (!product.skipAccessAnalysis && !radiusSearchFormGroup?.hiddenProducts.includes(product.id) && radiiOneMissing) {
              nextPathDisabledTooltipText = "Please input at least one radii if not hiding the radius search page";
            }

            if ((productOption?.defaultProducts.includes(product?.id) && productOption?.name === "")) {
              nextPathDisabledTooltipText = "Please input legal disclaimer text if defaulting the legal disclaimer checkbox";
            }

            step = (
              <AccessReportsAdminAddProductPageOptions
                disableUserActions={props.disableUserActions}
                invalidPageOptionPageElements={invalidPageOptionPageElements}
                onAddPageClick={onAddPageClick}
                onDisclaimerTextChange={onDisclaimerTextChange}
                onPageOptionPageChange={onPageOptionPageChange}
                onPageOptionPageToggle={onPageOptionPageToggle}
                onPageOptionToggle={onPageOptionToggle}
                onRemoveFormGroupClick={onRemoveFormGroupClick}
                onSaveAccessOverviewChartColorsClick={onSaveAccessOverviewChartColorsClick}
                onSaveAccessSummaryChartColorsClick={onSaveAccessSummaryChartColorsClick}
                onSaveColumnsClick={onSaveColumnsClick}
                product={product}
                template={template}
              />
            );
          } else {
            redirect = true;
          }

          break;
        default:
          break;
      }
    }

    if (!!product && redirect) {
      return <Redirect to={`/access-reports/admin/product/${product.id}/step/1`} />;
    } else {
      return (
        <AccessReportsAdminWizardStep
          currentWizardStep={currentWizardStep}
          nextPath={nextPath}
          nextPathDisabled={nextPathDisabled}
          nextPathDisabledTooltipText={nextPathDisabledTooltipText}
          onSaveProductClick={onSaveProductClick}
          previousPath={previousPath}
          setWizardStep={setWizardStep}
          wizardSteps={Object.values(AccessReportsAdminAddProductStepEnum)}
        >
          {step}
        </AccessReportsAdminWizardStep>
      );
    }
  }

  const extractProductDefaults = (): void => {
    const accessAnalysisPage: AccessReportsWizardPage = props.template.pages.find(x => x.title === AccessReportsWizardStepEnum.AccessAnalysis);
    const coverPage: AccessReportsWizardPage = props.template.pages.find(x => x.title === AccessReportsWizardStepEnum.CoverPage);
    const coverPageElements: AccessReportsWizardElement[] = flatten(coverPage?.formGroups.map(x => x.elements));
    const productElement: AccessReportsWizardElement = coverPageElements.find(x => x.elementId === AccessReportsCoverPageElementEnum.Product);

    let newFilterMemberSetByOutsideServiceArea = filterMemberSetByOutsideServiceArea;
    let newGroups: string[] = groups;
    let newHiddenCalculationMethods: number[] = hiddenCalculationMethods;
    let newHiddenDistanceMethods: number[] = hiddenDistanceMethods;
    let newHiddenAccessStandards: number[] = hiddenAccessStandards;
    let newProduct: AccessReportsWizardProduct = productElement?.options.find(x => x.id.toString() === props.productId.toString()) as AccessReportsWizardProduct;
    let newProductType: AccessReportsWizardOption = productType;
    let newProductTypes: AccessReportsWizardOption[] = productTypes;
    let newReportTitleOne: string = reportTitleOne;
    let newReportTitleTwo: string = reportTitleTwo;
    let newReportTitleThree: string = reportTitleThree;
    let newSelectedMemberSetServiceArea: AccessReportsServiceArea = selectedMemberSetServiceArea;
    let newTemplate: AccessReportsWizardTemplate = cloneDeep(props.template);
    const pageOptionsPage: AccessReportsWizardPage = getPageFromTemplate(newTemplate, AccessReportsWizardStepEnum.PageOptions);

    if (!!newProduct) {
      // editing a product that already exists within the template
      // align product name and display name
      if (newProduct.displayName) {
        newProduct.name = newProduct.displayName;
      } else {
        newProduct.displayName = newProduct.name;
      }
    } else {
      // adding a new product
      newProduct = getNewProductModel();
      newProduct.id = props.productId;
    }

    if (!!accessAnalysisPage && !newProduct.skipAccessAnalysis) {
      const accessAnalysisPageElements: AccessReportsWizardElement[] = flatten(accessAnalysisPage.formGroups.map(x => x.elements));

      for (const element of accessAnalysisPageElements) {
        switch (element.elementId) {
          case AccessReportsAccessAnalysisElementEnum.CalculationMethod:
            const hiddenCalculationMethodOptions: AccessReportsWizardOption[] = element.options.filter(x => x.hiddenProducts.includes(newProduct.id));

            if (hiddenCalculationMethodOptions.length) {
              newHiddenCalculationMethods = hiddenCalculationMethodOptions.map(x => x.id);
            }

            break;
          case AccessReportsAccessAnalysisElementEnum.DistanceMethod:
            const hiddenDistanceMethodOptions: AccessReportsWizardOption[] = element.options.filter(x => x.hiddenProducts.includes(newProduct.id));

            if (hiddenDistanceMethodOptions.length) {
              newHiddenDistanceMethods = hiddenDistanceMethodOptions.map(x => x.id);
            }

            break;
          case AccessReportsAccessAnalysisElementEnum.AccessStandard:
            const hiddenAccessStandardOptions: AccessReportsWizardOption[] = element.options.filter(x => x.hiddenProducts.includes(newProduct.id));

            if (hiddenAccessStandardOptions.length) {
              newHiddenAccessStandards = hiddenAccessStandardOptions.map(x => x.id);
            }

            break;
          default:
            break;
        }
      }
    }

    if (!!coverPage) {
      const coverPageElements: AccessReportsWizardElement[] = flatten(coverPage.formGroups.map(x => x.elements));

      for (const element of coverPageElements) {
        switch (element.elementId) {
          case AccessReportsCoverPageElementEnum.ReportTitleOne:
            const reportTitleOneOption: AccessReportsWizardOption = element.options.find(x => x.id === newProduct.id);

            if (!!reportTitleOneOption) {
              newReportTitleOne = reportTitleOneOption.displayName ? reportTitleOneOption.displayName : reportTitleOneOption.name;
            }

            break;
          case AccessReportsCoverPageElementEnum.ReportTitleTwo:
            const reportTitleTwoOption: AccessReportsWizardOption = element.options.find(x => x.id === newProduct.id);

            if (!!reportTitleTwoOption) {
              newReportTitleTwo = reportTitleTwoOption.displayName ? reportTitleTwoOption.displayName : reportTitleTwoOption.name;
            }

            break;
          case AccessReportsCoverPageElementEnum.ReportTitleThree:
            const reportTitleThreeOption: AccessReportsWizardOption = element.options.find(x => x.id === newProduct.id);

            if (!!reportTitleThreeOption) {
              newReportTitleThree = reportTitleThreeOption.displayName ? reportTitleThreeOption.displayName : reportTitleThreeOption.name;
            }

            break;
          case AccessReportsCoverPageElementEnum.Product:
            const productOption: AccessReportsWizardProduct = element.options.find(x => x.id === newProduct.id) as AccessReportsWizardProduct;

            if (!!productOption) {
              // groups
              if (!!newProduct.groups && newProduct.groups.length) {
                newGroups = cloneDeep(productOption.groups);
              } else {
                for (const accessCriteria of productOption.accessCriterion) {
                  if (!!accessCriteria.groups) {
                    for (const group of accessCriteria.groups) {
                      if (!newGroups.includes(group)) {
                        newGroups.push(group);
                      }
                    }
                  }
                }
              }

              // member set service area
              if (!!newProduct.accessCriterion && !newProduct.skipAccessAnalysis) {
                const firstAccessCriterion: AccessReportsWizardAccessCriteria = newProduct.accessCriterion[0];

                if (!!firstAccessCriterion) {
                  newFilterMemberSetByOutsideServiceArea = firstAccessCriterion.filterMemberSetByOutsideServiceArea;

                  if (!!firstAccessCriterion.memberSetServiceArea) {
                    newSelectedMemberSetServiceArea = firstAccessCriterion.memberSetServiceArea;
                  }
                }
              }
            }

            break;
          case AccessReportsCoverPageElementEnum.ProductType:
            const productTypeOption: AccessReportsWizardOption = element.options.find(x => newProduct.previousElementFilterId !== null &&
              newProduct.previousElementFilterId !== undefined &&
              x.id === newProduct.previousElementFilterId
            );

            if (!!productTypeOption) {
              newProductType = productTypeOption;
            }

            if (!!element.options) {
              newProductTypes = element.options;
            }

            break;
          default:
            break;
        }
      }
    }

    if (!!pageOptionsPage) {
      const pageOptionsFormGroups: AccessReportsWizardFormGroup[] = pageOptionsPage.formGroups.filter(x => !x.groupPropertyId);

      if (pageOptionsFormGroups.length > 0) {
        const pageOptionElements: AccessReportsWizardElement[] = flatten(pageOptionsFormGroups.map(x => x.elements));

        for (const element of pageOptionElements) {
          const pageOptionProductOption: AccessReportsWizardOption = element.options?.find(x => x.id === newProduct.id);

          if (!pageOptionProductOption) {
            const newOption: AccessReportsWizardOption = {
              defaultProducts: [],
              displayName: newProduct.displayName,
              hiddenProducts: [],
              id: newProduct.id,
              name: newProduct.name,
            };

            // separator and disclaimer page nodes will always be hidden from access reports wizard users
            if (element.elementId === AccessReportsPageOptionsElementEnum.SeparatorPage ||
              element.elementId === AccessReportsPageOptionsElementEnum.LegalDisclaimer
            ) {
              newOption.hiddenProducts = [newProduct.id];
              newOption.displayName = "";
              newOption.name = "";
            }

            if (!element.options) {
              element.options = [newOption];
            } else {
              element.options.push(newOption);
            }
          }
        }
      }

      newTemplate = buildNewPagesForProduct(newTemplate, newProduct.id, newProduct.skipAccessAnalysis);
    }

    setFilterMemberSetByOutsideServiceArea(newFilterMemberSetByOutsideServiceArea);
    setGroups(newGroups);
    setHiddenAccessStandards(newHiddenAccessStandards);
    setHiddenCalculationMethods(newHiddenCalculationMethods);
    setHiddenDistanceMethods(newHiddenDistanceMethods);
    setProduct(newProduct);
    setProductType(newProductType);
    setProductTypes(newProductTypes);
    setReportTitleOne(newReportTitleOne);
    setReportTitleTwo(newReportTitleTwo);
    setReportTitleThree(newReportTitleThree);
    setSelectedMemberSetServiceArea(newSelectedMemberSetServiceArea);
    setTemplate(newTemplate);
  }

  const getAccessStandardDefaultValues = (standardType: AccessReportsWizardNetworkStandardTypeEnum): AccessReportsWizardNetworkStandard => {
    switch (standardType) {
      case AccessReportsWizardNetworkStandardTypeEnum.Standard:
        return {
          miles: 10,
          minCount: 2,
          minutes: 20,
          type: standardType,
        };
      case AccessReportsWizardNetworkStandardTypeEnum.ZipUrban:
        return {
          miles: 5,
          minCount: 2,
          minutes: 10,
          type: standardType,
        };
      case AccessReportsWizardNetworkStandardTypeEnum.ZipSuburban:
        return {
          miles: 10,
          minCount: 2,
          minutes: 15,
          type: standardType,
        };
      case AccessReportsWizardNetworkStandardTypeEnum.ZipRural:
        return {
          miles: 25,
          minCount: 2,
          minutes: 40,
          type: standardType,
        };
      case AccessReportsWizardNetworkStandardTypeEnum.CountyLarge:
        return {
          miles: 5,
          minCount: 1,
          minutes: 10,
          type: standardType,
        };
      case AccessReportsWizardNetworkStandardTypeEnum.CountyMetro:
        return {
          miles: 10,
          minCount: 1,
          minutes: 15,
          type: standardType,
        };
      case AccessReportsWizardNetworkStandardTypeEnum.CountyMicro:
        return {
          miles: 20,
          minCount: 1,
          minutes: 30,
          type: standardType,
        };
      case AccessReportsWizardNetworkStandardTypeEnum.CountyRural:
        return {
          miles: 30,
          minCount: 1,
          minutes: 40,
          type: standardType,
        };
      case AccessReportsWizardNetworkStandardTypeEnum.CountyCEAC:
        return {
          miles: 60,
          minCount: 1,
          minutes: 70,
          type: standardType,
        };
      default:
        break;
    }
  }

  const getNewProductModel = (): AccessReportsWizardProduct => {
    const product: AccessReportsWizardProduct = {
      accessCriterion: [],
      defaultProducts: [],
      displayName: "",
      editable: false,
      groups: [],
      hiddenProducts: [],
      id: 0,
      lastUpdated: new Date(),
      name: "",
      previousElementFilterId: null,
      skipAccessAnalysis: false,
    };
    return product;
  }

  return (
    <Switch>
      <Route
        exact={true}
        path={`${match.url}/step/1`}
        render={() => createWizardStep(AccessReportsAdminAddProductStepEnum.ProductDetails, 0, `/access-reports/admin/product/${product?.id}/step/2`)}
      />
      <Route
        exact={true}
        path={`${match.url}/step/2`}
        render={() => createWizardStep(AccessReportsAdminAddProductStepEnum.Specialties, 1, `/access-reports/admin/product/${product?.id}/step/3`, null, `/access-reports/admin/product/${product?.id}/step/1`)}
      />
      <Route
        exact={true}
        path={`${match.url}/step/3`}
        render={() => createWizardStep(AccessReportsAdminAddProductStepEnum.PageOptions, 2, null, onSaveProductClick, `/access-reports/admin/product/${product?.id}/step/2`)}
      />
    </Switch>
  );
}

export { AccessReportsAdminAddProduct };
